import React, { useState, useEffect } from "react";
import {
  Box, Typography, Paper, Button, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Chip, InputAdornment, Grid, Collapse
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { db } from "../firebase";
import { salvaLog } from "../utils/logger";
import { useAuth } from "../context/AuthContext";
import {
  collection, onSnapshot, addDoc, deleteDoc,
  doc, query, where, orderBy, updateDoc, getDoc
} from "firebase/firestore";

function calcolaOrelavorate(oraInizio, oraFine, pausaMinuti) {
  if (!oraInizio || !oraFine) return 0;
  const [hI, mI] = oraInizio.split(":").map(Number);
  const [hF, mF] = oraFine.split(":").map(Number);
  const minutiTotali = (hF * 60 + mF) - (hI * 60 + mI) - (pausaMinuti || 0);
  return Math.max(0, minutiTotali / 60);
}

function formatOre(ore) {
  if (!ore || ore === 0) return "0h";
  const h = Math.floor(ore);
  const m = Math.round((ore - h) * 60);
  if (m > 0) return `${h}h ${m}m`;
  return `${h}h`;
}

function RigaCollaboratore({ p, onRimuovi, onAggiornaTotaleOre }) {
  const [aperta, setAperta] = useState(false);
  const [giornate, setGiornate] = useState(p.giornate || []);
  const [salvando, setSalvando] = useState(false);

  const totaleOre = giornate.reduce((acc, g) => acc + calcolaOrelavorate(g.oraInizio, g.oraFine, g.pausaMinuti), 0);

  useEffect(() => {
    onAggiornaTotaleOre(p.id, totaleOre);
  }, [totaleOre]);

  function aggiungiGiornata() {
    setGiornate((prev) => [...prev, { data: "", oraInizio: "", oraFine: "", pausaMinuti: 0 }]);
  }

  function rimuoviGiornata(idx) {
    setGiornate((prev) => prev.filter((_, i) => i !== idx));
  }

  function handleGiornata(idx, field, value) {
    setGiornate((prev) => {
      const nuove = [...prev];
      nuove[idx] = { ...nuove[idx], [field]: value };
      return nuove;
    });
  }

  async function handleSalvaGiornate() {
    setSalvando(true);
    try {
      await updateDoc(doc(db, "personaleEvento", p.id), { giornate });
    } catch (err) {
      console.error("Errore salvataggio giornate:", err);
    }
    setSalvando(false);
  }

  return (
    <>
      <TableRow hover>
        <TableCell sx={{ width: 40 }}>
          <IconButton size="small" onClick={() => setAperta(!aperta)}>
            {aperta ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{p.nominativo}</TableCell>
        <TableCell>{p.specializzazione || "—"}</TableCell>
        <TableCell align="center">
          <Chip label={formatOre(totaleOre)} size="small"
            color={totaleOre > 0 ? "primary" : "default"} variant="outlined" />
        </TableCell>
        <TableCell align="center">
          <IconButton size="small" color="error" onClick={() => onRimuovi(p.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, backgroundColor: "#fafafa" }}>
          <Collapse in={aperta} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Giornate lavorative
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={aggiungiGiornata}>
                  Aggiungi giornata
                </Button>
              </Box>

              {giornate.length === 0 ? (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Nessuna giornata inserita.
                </Typography>
              ) : (
                giornate.map((g, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Data" type="date"
                          value={g.data || ""}
                          onChange={(e) => handleGiornata(idx, "data", e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Ora inizio" type="time"
                          value={g.oraInizio || ""}
                          onChange={(e) => handleGiornata(idx, "oraInizio", e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Ora fine" type="time"
                          value={g.oraFine || ""}
                          onChange={(e) => handleGiornata(idx, "oraFine", e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Pausa (min)" type="number"
                          value={g.pausaMinuti || ""}
                          onChange={(e) => handleGiornata(idx, "pausaMinuti", parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0 }} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          {formatOre(calcolaOrelavorate(g.oraInizio, g.oraFine, g.pausaMinuti))}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton size="small" color="error" onClick={() => rimuoviGiornata(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Typography variant="body2" fontWeight="bold">
                  Totale ore: <span style={{ color: "#1A56A0" }}>{formatOre(totaleOre)}</span>
                </Typography>
                <Button variant="contained" size="small" onClick={handleSalvaGiornate}
                  disabled={salvando}>
                  {salvando ? <CircularProgress size={16} /> : "Salva giornate"}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function PersonaleEvento({ eventoId, onOreCollaboratoriChange }) {
  const [personaleIngaggiato, setPersonaleIngaggiato] = useState([]);
  const [collaboratori, setCollaboratori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ricerca, setRicerca] = useState("");
  const [saving, setSaving] = useState(false);
  const [orePerCollaboratore, setOrePerCollaboratore] = useState({});
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!eventoId) return;
    const q = query(collection(db, "personaleEvento"), where("eventoId", "==", eventoId));
    const unsub = onSnapshot(q, (snap) => {
      setPersonaleIngaggiato(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [eventoId]);

  useEffect(() => {
    const q = query(collection(db, "collaboratori"), orderBy("nominativo"));
    const unsub = onSnapshot(q, (snap) => {
      setCollaboratori(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  // Aggiorna oreCollaboratori nell'evento quando cambia il totale
  useEffect(() => {
    const totale = Object.values(orePerCollaboratore).reduce((acc, ore) => acc + ore, 0);
    const totaleArrotondato = Math.round(totale * 100) / 100;
    if (onOreCollaboratoriChange) {
      onOreCollaboratoriChange(totaleArrotondato);
    }
    // Aggiorna Firestore
    if (eventoId) {
      updateDoc(doc(db, "eventi", eventoId), {
        oreCollaboratori: String(totaleArrotondato)
      }).catch((e) => console.error("Errore aggiornamento ore collaboratori:", e));
    }
  }, [orePerCollaboratore]);

  function handleAggiornaTotaleOre(personaleId, ore) {
    setOrePerCollaboratore((prev) => ({ ...prev, [personaleId]: ore }));
  }

  const idIngaggiati = personaleIngaggiato.map((p) => p.collaboratoreId);

  const collaboratoriFiltrati = collaboratori.filter((c) => {
    const s = ricerca.toLowerCase();
    const spec = Array.isArray(c.specializzazione)
      ? c.specializzazione.join(" ")
      : c.specializzazione || "";
    return (
      c.nominativo?.toLowerCase().includes(s) ||
      spec.toLowerCase().includes(s)
    );
  });

  async function handleIngaggia(collaboratore) {
    if (idIngaggiati.includes(collaboratore.id)) return;
    setSaving(true);
    try {
      await addDoc(collection(db, "personaleEvento"), {
        eventoId,
        collaboratoreId: collaboratore.id,
        nominativo: collaboratore.nominativo,
        specializzazione: collaboratore.specializzazione || "",
        tipologiaFornitore: collaboratore.tipologiaFornitore || "",
        giornate: [],
      });

      const eventiPassati = collaboratore.eventiPassati || [];
      if (!eventiPassati.includes(eventoId)) {
        await updateDoc(doc(db, "collaboratori", collaboratore.id), {
          eventiPassati: [...eventiPassati, eventoId],
        });
      }

      salvaLog(currentUser, "Aggiunta collaboratore", eventoId, "", [
        { campo: "Collaboratore", da: "—", a: collaboratore.nominativo }
      ]);
    } catch (error) {
      console.error("Errore ingaggio:", error);
    }
    setSaving(false);
  }

  async function handleRimuovi(id) {
    const p = personaleIngaggiato.find((p) => p.id === id);
    if (!window.confirm(`Rimuovere ${p?.nominativo} dall'evento?`)) return;
    try {
      await deleteDoc(doc(db, "personaleEvento", id));
      salvaLog(currentUser, "Rimozione collaboratore", eventoId, "", [
        { campo: "Collaboratore", da: p?.nominativo || "—", a: "—" }
      ]);
    } catch (error) {
      console.error("Errore rimozione:", error);
    }
  }

  if (loading) return <CircularProgress size={24} />;

  const totaleComplessivo = Object.values(orePerCollaboratore).reduce((acc, ore) => acc + ore, 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight="bold" color="primary">
            Personale ingaggiato
          </Typography>
          {totaleComplessivo > 0 && (
            <Typography variant="body2" color="text.secondary">
              Totale ore collaboratori: <strong>{formatOre(totaleComplessivo)}</strong>
            </Typography>
          )}
        </Box>
        <Button variant="outlined" size="small" startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}>
          Aggiungi
        </Button>
      </Box>

      {personaleIngaggiato.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nessun collaboratore ingaggiato per questo evento.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                <TableCell sx={{ width: 40 }} />
                <TableCell><strong>Nominativo</strong></TableCell>
                <TableCell><strong>Specializzazione</strong></TableCell>
                <TableCell align="center"><strong>Ore totali</strong></TableCell>
                <TableCell align="center"><strong>Rimuovi</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personaleIngaggiato.map((p) => (
                <RigaCollaboratore
                  key={p.id}
                  p={p}
                  onRimuovi={handleRimuovi}
                  onAggiornaTotaleOre={handleAggiornaTotaleOre}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* DIALOG SELEZIONE COLLABORATORI */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Seleziona collaboratori</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Cerca per nominativo o ruolo..."
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
            sx={{ mb: 2, mt: 1 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>Nominativo</strong></TableCell>
                  <TableCell><strong>Specializzazione</strong></TableCell>
                  <TableCell align="center"><strong>Stato</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {collaboratoriFiltrati.map((c) => {
                  const giàIngaggiato = idIngaggiati.includes(c.id);
                  return (
                    <TableRow key={c.id} hover
                      sx={{ cursor: giàIngaggiato ? "default" : "pointer", opacity: giàIngaggiato ? 0.5 : 1 }}
                      onClick={() => !giàIngaggiato && handleIngaggia(c)}>
                      <TableCell>{c.nominativo}</TableCell>
                      <TableCell>{c.specializzazione || "—"}</TableCell>
                      <TableCell align="center">
                        {giàIngaggiato ? (
                          <Chip label="Ingaggiato" size="small" color="success" />
                        ) : (
                          <Chip label="Aggiungi" size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}